import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { warn as devWarn } from "@/lib/devlog";
import {
  CalendarRange, Plus, Trash2, CheckCircle2, Power, PowerOff, Sparkles, AlertCircle, Coins, Clock, Wallet,
  ShieldCheck, KeyRound, Lock, Loader2, Check, Mail, Send,
} from "lucide-react";

export default function SettingsPage() {
  const { selectedBranch } = useBranch();

  return (
    <>
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Settings</h1>
        <p className="text-brand-inkMuted mt-1">
          {selectedBranch
            ? <>Managing settings for <span className="font-semibold text-foreground">{selectedBranch.name}</span></>
            : "Pick a branch from the header to manage its settings."}
        </p>
      </div>

      <Tabs defaultValue="school-year" className="w-full">
        <TabsList className="bg-brand-purpleLight rounded-full p-1 h-auto">
          <TabsTrigger
            value="school-year"
            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-brand-purpleHover"
            data-testid="settings-tab-school-year"
          >
            <CalendarRange className="w-4 h-4 mr-1" /> School Year
          </TabsTrigger>
          <TabsTrigger
            value="preferences"
            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-brand-purpleHover"
            data-testid="settings-tab-preferences"
          >
            <Coins className="w-4 h-4 mr-1" /> Preferences
          </TabsTrigger>
          <TabsTrigger
            value="account"
            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-brand-purpleHover"
            data-testid="settings-tab-account"
          >
            <ShieldCheck className="w-4 h-4 mr-1" /> Account
          </TabsTrigger>
          <TabsTrigger
            value="email-notifications"
            className="rounded-full px-5 py-2 data-[state=active]:bg-white data-[state=active]:text-brand-purpleHover"
            data-testid="settings-tab-email-notifications"
          >
            <Mail className="w-4 h-4 mr-1" /> Email Notification
          </TabsTrigger>
        </TabsList>

        <TabsContent value="school-year" className="mt-6">
          {selectedBranch ? (
            <SchoolYearTab branch={selectedBranch} />
          ) : (
            <NoBranchPicked />
          )}
        </TabsContent>

        <TabsContent value="preferences" className="mt-6">
          <PreferencesTab />
        </TabsContent>

        <TabsContent value="account" className="mt-6">
          <AccountTab />
        </TabsContent>

        <TabsContent value="email-notifications" className="mt-6">
          <EmailNotificationsTab />
        </TabsContent>
      </Tabs>
    </>
  );
}

function NoBranchPicked() {
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
        <AlertCircle className="w-7 h-7 text-[#E65100]" />
      </div>
      <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
      <p className="text-brand-inkMuted mt-1">
        School years are managed per branch. Use the school/branch dropdown in the header to choose one.
      </p>
      <Link to="/branches" className="inline-block mt-4">
        <Button variant="outline" className="rounded-full border-brand-purple/30 text-brand-purple hover:bg-brand-purpleLight">
          Manage branches
        </Button>
      </Link>
    </div>
  );
}

function SchoolYearTab({ branch }) {
  const currentYear = new Date().getFullYear();
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ start_year: String(currentYear), end_year: String(currentYear + 1) });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/school-years", { params: { branch_id: branch.id } });
      setYears(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [branch.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/school-years", {
        branch_id: branch.id,
        start_year: parseInt(form.start_year, 10),
        end_year: parseInt(form.end_year, 10),
      });
      toast.success(`School year ${form.start_year}-${form.end_year} created for ${branch.name}`);
      setOpenCreate(false);
      setForm({ start_year: String(currentYear), end_year: String(currentYear + 1) });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (id, label) => {
    try {
      await api.post(`/school-years/${id}/activate`);
      toast.success(`Loaded ${label} — records for this branch are now active`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deactivate = async (id, label) => {
    try {
      await api.post(`/school-years/${id}/deactivate`);
      toast.success(`Unloaded ${label}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      const { data } = await api.delete(`/school-years/${deleteTarget.id}`);
      const note = (data.deleted_classes + data.deleted_students + data.deleted_events) > 0
        ? ` (removed ${data.deleted_classes} classes, ${data.deleted_students} students, ${data.deleted_events} events)`
        : "";
      toast.success(`Deleted ${deleteTarget.label}${note}`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startYearOptions = [];
  for (let y = currentYear - 5; y <= currentYear + 10; y++) startYearOptions.push(y);

  return (
    <section data-testid="school-year-section">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold text-foreground">School Years</h2>
          <p className="text-sm text-brand-inkMuted">
            Activate a school year for <span className="font-semibold text-foreground">{branch.name}</span> before enrolling
            students or creating classes & events. All records here are tied to the active year of this branch.
          </p>
        </div>
        <Button
          onClick={() => setOpenCreate(true)}
          className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm"
          data-testid="add-school-year-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Add school year
        </Button>
      </div>

      {loading ? (
        <div className="text-brand-inkMuted font-heading">Loading...</div>
      ) : years.length === 0 ? (
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center" data-testid="no-school-years">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
            <CalendarRange className="w-7 h-7 text-brand-purple" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">No school years yet for this branch</p>
          <p className="text-brand-inkMuted">Create the first one to start enrolling students and adding classes.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {years.map((y) => (
            <SchoolYearCard
              key={y.id}
              year={y}
              onActivate={() => activate(y.id, y.label)}
              onDeactivate={() => deactivate(y.id, y.label)}
              onDelete={() => setDeleteTarget(y)}
            />
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="rounded-3xl border-brand-purple/20 max-w-md" data-testid="create-school-year-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand-purple" />
              New school year
            </DialogTitle>
            <DialogDescription>
              For branch <strong>{branch.name}</strong>. Pick a start year and end year (e.g. 2026 to 2027).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="create-school-year-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-heading">Start year</Label>
                <select
                  value={form.start_year}
                  onChange={(e) => {
                    const sy = e.target.value;
                    setForm({ start_year: sy, end_year: String(parseInt(sy, 10) + 1) });
                  }}
                  className="w-full mt-1.5 h-12 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
                  data-testid="start-year-select"
                >
                  {startYearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="font-heading">End year</Label>
                <Input
                  type="number"
                  required
                  value={form.end_year}
                  onChange={(e) => setForm({ ...form, end_year: e.target.value })}
                  className="rounded-2xl h-12 mt-1.5 border-brand-purple/20"
                  data-testid="end-year-input"
                />
              </div>
            </div>
            <div className="rounded-2xl bg-brand-purpleLight px-4 py-2 text-sm text-brand-purpleHover font-semibold">
              Preview: {form.start_year}-{form.end_year}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenCreate(false)}
                className="rounded-full border-brand-purple/30">
                Cancel
              </Button>
              <Button type="submit" disabled={busy}
                className="rounded-full bg-brand-purple hover:bg-brand-purpleHover text-white"
                data-testid="create-school-year-submit">
                {busy ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-3xl border-brand-purple/20" data-testid="delete-school-year-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete {deleteTarget?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>permanently delete</strong> all classes, students and events tied to{" "}
              <span className="font-semibold text-foreground">{deleteTarget?.label}</span> for branch{" "}
              <span className="font-semibold text-foreground">{branch.name}</span>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full border-brand-purple/30">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              className="rounded-full bg-[#C62828] hover:bg-[#B71C1C] text-white"
              data-testid="confirm-delete-school-year"
            >
              Delete year + records
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function SchoolYearCard({ year, onActivate, onDeactivate, onDelete }) {  return (
    <div
      className={`relative bg-white rounded-3xl border shadow-sm p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-300 ${
        year.is_active ? "border-brand-purple ring-2 ring-brand-purple/30" : "border-brand-purple/10"
      }`}
      data-testid={`school-year-card-${year.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
          year.is_active ? "bg-brand-purple text-white" : "bg-brand-purpleLight text-brand-purple"
        }`}>
          <CalendarRange className="w-5 h-5" />
        </div>
        {year.is_active ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-[#E8F5E9] text-[#2E7D32]"
            data-testid={`active-badge-${year.id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-brand-purpleLight text-brand-inkMuted">
            Inactive
          </span>
        )}
      </div>

      <h3 className="font-heading text-2xl font-semibold text-foreground" data-testid={`school-year-label-${year.id}`}>
        {year.label}
      </h3>
      <p className="text-xs text-brand-inkMuted mt-1">
        Created {new Date(year.created_at).toLocaleDateString()}
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {year.is_active ? (
          <Button
            onClick={onDeactivate}
            variant="outline"
            className="rounded-full border-brand-purple/30 text-brand-purple hover:bg-brand-purpleLight"
            data-testid={`deactivate-btn-${year.id}`}
          >
            <PowerOff className="w-4 h-4 mr-1" /> Unload
          </Button>
        ) : (
          <Button
            onClick={onActivate}
            className="rounded-full bg-[#81C784] hover:bg-[#66BB6A] text-white"
            data-testid={`activate-btn-${year.id}`}
          >
            <Power className="w-4 h-4 mr-1" /> Activate
          </Button>
        )}
        <Button
          onClick={onDelete}
          variant="outline"
          className="rounded-full border-[#F06292] text-[#C2185B] hover:bg-[#FFEBEE]"
          data-testid={`delete-btn-${year.id}`}
        >
          <Trash2 className="w-4 h-4 mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}


const CURRENCY_OPTIONS = [
  { symbol: "₱", name: "Philippine Peso (₱)" },
  { symbol: "$", name: "US Dollar ($)" },
  { symbol: "€", name: "Euro (€)" },
  { symbol: "£", name: "British Pound (£)" },
  { symbol: "¥", name: "Japanese Yen / Chinese Yuan (¥)" },
  { symbol: "₹", name: "Indian Rupee (₹)" },
  { symbol: "A$", name: "Australian Dollar (A$)" },
  { symbol: "C$", name: "Canadian Dollar (C$)" },
];

function PreferencesTab() {
  return (
    <div className="space-y-5">
      <CurrencyPreference />
      <SalaryFrequencyPreference />
      <AttendancePreference />
    </div>
  );
}

function CurrencyPreference() {
  const { user, updateUser } = useAuth();
  const [symbol, setSymbol] = useState(user?.currency_symbol || "₱");
  const [busy, setBusy] = useState(false);
  const isCustom = !CURRENCY_OPTIONS.some((c) => c.symbol === symbol);

  const save = async (newSymbol) => {
    if (!newSymbol) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/owner-settings", { currency_symbol: newSymbol });
      updateUser({ currency_symbol: data.currency_symbol });
      toast.success(`Default currency set to ${data.currency_symbol}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6 max-w-2xl" data-testid="preferences-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-2xl bg-brand-purpleLight flex items-center justify-center">
          <Coins className="w-5 h-5 text-brand-purple" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground">Default currency</h2>
          <p className="text-sm text-brand-inkMuted">Used wherever amounts are displayed across SchoolBoss.</p>
        </div>
      </div>

      <div className="space-y-3">
        <Label className="font-heading">Pick a currency symbol</Label>
        <div className="flex flex-wrap gap-2" data-testid="currency-options">
          {CURRENCY_OPTIONS.map((c) => (
            <button
              key={c.symbol}
              type="button"
              onClick={() => { setSymbol(c.symbol); save(c.symbol); }}
              className={`px-4 py-2 rounded-2xl border text-sm font-semibold transition-all ${
                symbol === c.symbol
                  ? "border-brand-purple bg-brand-purple text-white shadow-sm"
                  : "border-brand-purple/20 hover:bg-brand-purpleLight text-foreground"
              }`}
              data-testid={`currency-${c.symbol}`}
            >
              <span className="text-lg mr-1">{c.symbol}</span>
              <span className="text-xs opacity-80">{c.name.replace(/\s*\([^)]+\)\s*$/, "")}</span>
            </button>
          ))}
        </div>

        <div className="pt-3">
          <Label className="font-heading">Or use a custom symbol</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input
              value={isCustom ? symbol : ""}
              onChange={(e) => setSymbol(e.target.value.slice(0, 4))}
              placeholder="₿"
              maxLength={4}
              className="rounded-2xl h-11 w-32 border-brand-purple/20 text-center text-lg"
              data-testid="currency-custom-input"
            />
            <Button
              onClick={() => save(symbol)}
              disabled={busy || !symbol}
              className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"
              data-testid="currency-custom-save"
            >
              Save
            </Button>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-brand-purpleLight px-4 py-3 text-sm">
          <span className="text-brand-inkMuted">Preview: </span>
          <span className="font-heading font-semibold text-foreground" data-testid="currency-preview">
            {user?.currency_symbol || symbol} 12,500.00
          </span>
        </div>
      </div>
    </section>
  );
}

const INTERVAL_OPTIONS = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
];

function buildHourOptions() {
  const out = [];
  for (let h = 0; h < 24; h += 1) out.push(`${String(h).padStart(2, "0")}:00`);
  return out;
}
const HOUR_OPTIONS = buildHourOptions();

function AttendancePreference() {
  const { user, updateUser } = useAuth();
  const [start, setStart] = useState(user?.attendance_start || "07:00");
  const [end, setEnd] = useState(user?.attendance_end || "22:00");
  const [interval, setInterval] = useState(user?.attendance_interval_minutes || 30);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (start >= end) {
      toast.error("Start time must be earlier than end time");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch("/owner-settings", {
        attendance_start: start,
        attendance_end: end,
        attendance_interval_minutes: Number(interval),
      });
      updateUser({
        attendance_start: data.attendance_start,
        attendance_end: data.attendance_end,
        attendance_interval_minutes: data.attendance_interval_minutes,
      });
      toast.success("Attendance settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6 max-w-2xl" data-testid="attendance-preference-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-2xl bg-brand-purpleLight flex items-center justify-center">
          <Clock className="w-5 h-5 text-brand-purple" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground">Attendance time slots</h2>
          <p className="text-sm text-brand-inkMuted">
            Choose the time range and interval used in the Employee Attendance module.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label className="font-heading">Start time</Label>
          <select
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="w-full mt-1.5 h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
            data-testid="attendance-start-select"
          >
            {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div>
          <Label className="font-heading">End time</Label>
          <select
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="w-full mt-1.5 h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
            data-testid="attendance-end-select"
          >
            {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div>
          <Label className="font-heading">Interval</Label>
          <select
            value={interval}
            onChange={(e) => setInterval(Number(e.target.value))}
            className="w-full mt-1.5 h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 font-body text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
            data-testid="attendance-interval-select"
          >
            {INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-brand-purpleLight px-4 py-3 text-sm">
        <span className="text-brand-inkMuted">Preview: </span>
        <span className="font-heading font-semibold text-foreground" data-testid="attendance-preview">
          {start} – {end} · every {INTERVAL_OPTIONS.find((o) => o.value === Number(interval))?.label}
        </span>
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={save}
          disabled={busy}
          className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white"
          data-testid="attendance-settings-save"
        >
          {busy ? "Saving..." : "Save attendance settings"}
        </Button>
      </div>
    </section>
  );
}



function SalaryFrequencyPreference() {
  const { user, updateUser } = useAuth();
  const [freq, setFreq] = useState(user?.salary_frequency || "twice_monthly");
  const [busy, setBusy] = useState(false);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/owner-settings", { salary_frequency: next });
      updateUser({ salary_frequency: data.salary_frequency });
      setFreq(data.salary_frequency);
      toast.success(`Salary frequency set to ${data.salary_frequency === "monthly" ? "Monthly" : "Twice Monthly"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const Option = ({ value, title, hint, testId }) => (
    <button
      type="button"
      disabled={busy}
      onClick={() => save(value)}
      className={`flex-1 text-left rounded-2xl border p-4 transition-all ${
        freq === value
          ? "border-brand-purple bg-brand-purpleLight/60 shadow-sm"
          : "border-brand-purple/15 hover:border-brand-purple/40 hover:bg-brand-purpleLight/30"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span className="font-heading font-semibold text-foreground">{title}</span>
        {freq === value && <CheckCircle2 className="w-5 h-5 text-brand-purple" />}
      </div>
      <p className="text-sm text-brand-inkMuted mt-1">{hint}</p>
    </button>
  );

  return (
    <section className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-6 max-w-2xl" data-testid="salary-frequency-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-2xl bg-brand-purpleLight flex items-center justify-center">
          <Wallet className="w-5 h-5 text-brand-purple" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground">Salary frequency</h2>
          <p className="text-sm text-brand-inkMuted">
            Controls how salary schedules appear in the Employee Salary Module.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Option
          value="twice_monthly"
          title="Twice Monthly"
          hint="Income: Calculated Daily, Payable on 1st Half, Payable on 2nd Half · Deduction: 1st Half / 2nd Half"
          testId="salary-freq-twice-monthly"
        />
        <Option
          value="monthly"
          title="Monthly"
          hint="Income: Calculated Daily or Payable Monthly · Deduction: automatically applied monthly"
          testId="salary-freq-monthly"
        />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Account tab — change password (works for school_owner, branch_admin, etc.)
// ---------------------------------------------------------------------------
function AccountTab() {
  const { user } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (newPw !== confirm) { setError("New password and confirmation do not match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated successfully.");
      setCurrentPw(""); setNewPw(""); setConfirm("");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="bg-white rounded-3xl border border-brand-purple/10 shadow-sm p-7" data-testid="account-card">
        <div className="flex items-start gap-3 mb-5">
          <span className="w-11 h-11 rounded-2xl bg-[#DBEAFE] flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-[#2563EB]" />
          </span>
          <div>
            <h2 className="font-heading text-xl font-bold">Change Password</h2>
            <p className="text-sm text-brand-inkMuted mt-1 leading-relaxed">
              Signed in as <span className="font-semibold text-foreground">{user?.email}</span>. Pick a strong password you don't use anywhere else. You'll stay signed in on this device after the change.
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="account-pw-form">
          <AccountPwField label="Current password" value={currentPw} onChange={setCurrentPw} testid="account-pw-current" />
          <AccountPwField label="New password" value={newPw} onChange={setNewPw} testid="account-pw-new"
            hint="At least 8 characters. Mix letters, numbers and symbols for best results." />
          <AccountPwField label="Confirm new password" value={confirm} onChange={setConfirm} testid="account-pw-confirm" />

          {error && (
            <div className="rounded-2xl bg-[#FFEBEE] text-[#C62828] px-4 py-3 text-sm" data-testid="account-pw-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={saving || !currentPw || !newPw || !confirm}
            className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-11 px-6 font-heading font-semibold shadow-sm"
            data-testid="account-pw-save-btn">
            {saving
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Updating…</>
              : <><Check className="w-4 h-4 mr-1.5" /> Update password</>}
          </Button>
        </form>
      </div>
    </div>
  );
}

const AccountPwField = ({ label, value, onChange, hint, testid }) => (
  <div>
    <Label className="font-heading text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="relative mt-1.5">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
      <Input type="password" value={value} onChange={(e) => onChange(e.target.value)}
        required minLength={8}
        className="rounded-2xl h-12 pl-10"
        data-testid={testid} />
    </div>
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);


/* -------------------------------------------------------------------------- */
/* Email Notifications Tab                                                    */
/* -------------------------------------------------------------------------- */
function EmailNotificationsTab() {
  const { branchesForSelectedSchool } = useBranch();
  const [cfg, setCfg] = useState(null); // owner-level Gmail config
  const [loadingCfg, setLoadingCfg] = useState(true);

  const loadCfg = useCallback(async () => {
    setLoadingCfg(true);
    try {
      const { data } = await api.get("/owner/email-config");
      setCfg(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoadingCfg(false); }
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  return (
    <div className="space-y-6">
      <GmailAccountCard cfg={cfg} loading={loadingCfg} onSaved={loadCfg} data-testid="email-gmail-card" />

      <div className="bg-white rounded-3xl border border-brand-purple/10 p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="font-heading text-xl font-semibold text-foreground">Daily Cash Report — per Branch</h3>
            <p className="text-sm text-brand-inkMuted mt-1">
              Set the local send time (Asia/Manila) and the recipient emails for each branch. The system will email a PDF + summary at the configured time every day.
            </p>
          </div>
        </div>

        {!cfg?.configured && (
          <div className="rounded-2xl bg-[#FFF8E1] border border-[#FFE082] p-4 mb-4 flex gap-3" data-testid="email-config-warn">
            <AlertCircle className="w-5 h-5 text-[#E65100] shrink-0 mt-0.5" />
            <p className="text-sm text-[#5D4037]">Configure your Gmail account above before enabling per-branch delivery — without it, the scheduler has nothing to send through.</p>
          </div>
        )}

        {branchesForSelectedSchool.length === 0 ? (
          <p className="text-brand-inkMuted text-sm">No branches in the selected school yet.</p>
        ) : (
          <div className="space-y-3" data-testid="email-branches-list">
            {branchesForSelectedSchool.map((b) => (
              <BranchEmailRow key={b.id} branch={b} ownerConfigured={!!cfg?.configured} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GmailAccountCard({ cfg, loading, onSaved }) {
  const [gmail, setGmail] = useState("");
  const [appPw, setAppPw] = useState("");
  const [sender, setSender] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (cfg) {
      setGmail(cfg.gmail_address || "");
      setSender(cfg.sender_display_name || "");
    }
  }, [cfg]);

  const save = async (e) => {
    e.preventDefault();
    if (!gmail.trim()) { toast.error("Gmail address is required"); return; }
    if (!cfg?.configured && !appPw.trim()) { toast.error("App Password is required for the first setup"); return; }
    setSaving(true);
    try {
      await api.put("/owner/email-config", {
        gmail_address: gmail.trim(),
        gmail_app_password: appPw,
        sender_display_name: sender.trim(),
      });
      toast.success(cfg?.configured ? "Gmail settings updated" : "Gmail account saved");
      setAppPw("");
      setEditing(false);
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="bg-white rounded-3xl border border-brand-purple/10 p-6 text-brand-inkMuted">Loading…</div>;
  }

  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-6" data-testid="email-gmail-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-heading text-xl font-semibold text-foreground flex items-center gap-2">
            <Mail className="w-5 h-5 text-brand-purple" /> Gmail Account
          </h3>
          <p className="text-sm text-brand-inkMuted mt-1">
            Used as the From address for every notification email. Use a <strong>Google App Password</strong> (16 chars) — generate one at{" "}
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline text-brand-purple">myaccount.google.com/apppasswords</a> (2-Step Verification must be ON).
          </p>
        </div>
        {cfg?.configured && !editing && (
          <span className="rounded-full bg-[#E8F5E9] text-[#2E7D32] px-3 py-1 text-xs font-heading flex items-center gap-1.5 shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5" /> Configured
          </span>
        )}
      </div>

      {cfg?.configured && !editing ? (
        <div className="space-y-2 text-sm">
          <div><span className="text-brand-inkMuted">From:</span> <span className="font-medium">{cfg.gmail_address}</span></div>
          {cfg.sender_display_name && <div><span className="text-brand-inkMuted">Display name:</span> <span className="font-medium">{cfg.sender_display_name}</span></div>}
          <div className="pt-2">
            <Button variant="outline" onClick={() => setEditing(true)} className="rounded-2xl" data-testid="email-gmail-edit">
              <KeyRound className="w-4 h-4 mr-1.5" /> Update credentials
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="font-heading">Gmail address <span className="text-[#C62828]">*</span></Label>
            <Input type="email" value={gmail} onChange={(e) => setGmail(e.target.value)} placeholder="you@gmail.com" className="rounded-2xl h-11 mt-1.5" data-testid="email-gmail-address" autoComplete="off" />
          </div>
          <div>
            <Label className="font-heading">Sender display name</Label>
            <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="School Name" className="rounded-2xl h-11 mt-1.5" data-testid="email-gmail-sender" autoComplete="off" />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-heading">App Password {cfg?.configured ? <span className="text-brand-inkMuted text-xs">(leave blank to keep current)</span> : <span className="text-[#C62828]">*</span>}</Label>
            <Input type="password" value={appPw} onChange={(e) => setAppPw(e.target.value)} placeholder="16-character app password" className="rounded-2xl h-11 mt-1.5" data-testid="email-gmail-apppw" autoComplete="new-password" />
          </div>
          <div className="sm:col-span-2 flex gap-2 justify-end">
            {cfg?.configured && <Button type="button" variant="outline" onClick={() => { setEditing(false); setAppPw(""); }} className="rounded-2xl">Cancel</Button>}
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple text-white" data-testid="email-gmail-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <>Save Gmail Account</>}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function BranchEmailRow({ branch, ownerConfigured }) {
  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState("21:00");
  const [recipients, setRecipients] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/branches/${branch.id}/email-notifications`);
        const dcr = data.daily_cash_report || {};
        setEnabled(!!dcr.enabled);
        setTime(dcr.send_time_local || "21:00");
        setRecipients(dcr.recipients || []);
      } catch (e) { devWarn(e); }
      finally { setLoaded(true); }
    })();
  }, [branch.id]);

  const addRecipient = () => {
    const v = newEmail.trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error("Invalid email address"); return; }
    if (recipients.includes(v)) { toast.error("Already in the list"); return; }
    setRecipients([...recipients, v]);
    setNewEmail("");
  };

  const removeRecipient = (e) => setRecipients(recipients.filter((x) => x !== e));

  const save = async () => {
    if (enabled && recipients.length === 0) { toast.error("Add at least one recipient when enabled"); return; }
    setSaving(true);
    try {
      await api.put(`/branches/${branch.id}/email-notifications`, {
        daily_cash_report: { enabled, send_time_local: time, recipients },
      });
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!ownerConfigured) { toast.error("Configure your Gmail account first"); return; }
    if (recipients.length === 0) { toast.error("Add at least one recipient first"); return; }
    setTesting(true);
    try {
      const { data } = await api.post(`/branches/${branch.id}/email-notifications/test`, { recipients });
      toast.success(`Test email sent to ${data.sent_to.length} recipient${data.sent_to.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  if (!loaded) {
    return <div className="rounded-2xl border border-brand-purple/10 bg-[#F6F8FE]/40 p-4 text-sm text-brand-inkMuted">Loading {branch.name}…</div>;
  }

  return (
    <div className="rounded-2xl border border-brand-purple/15 bg-[#F6F8FE]/30 p-4 sm:p-5" data-testid={`email-branch-row-${branch.id}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white border border-brand-purple/15 flex items-center justify-center">
            <Mail className="w-4 h-4 text-brand-purple" />
          </div>
          <div>
            <p className="font-heading text-base font-semibold text-foreground">{branch.name}</p>
            <p className="text-[11px] text-brand-inkMuted uppercase tracking-wider font-heading">Daily Cash Report</p>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-[#5D2E8E]"
            data-testid={`email-branch-toggle-${branch.id}`}
          />
          <span className="text-sm font-medium">{enabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>

      <div className="grid sm:grid-cols-[180px_1fr] gap-4">
        <div>
          <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Send time (PH)</Label>
          <Input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="rounded-2xl h-11 mt-1.5 bg-white"
            disabled={!enabled}
            data-testid={`email-branch-time-${branch.id}`}
          />
        </div>
        <div>
          <Label className="font-heading text-xs uppercase tracking-wider text-brand-inkMuted">Recipients</Label>
          <div className="mt-1.5 flex gap-2">
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
              placeholder="admin@example.com"
              className="rounded-2xl h-11 bg-white"
              disabled={!enabled}
              data-testid={`email-branch-input-${branch.id}`}
            />
            <Button type="button" onClick={addRecipient} disabled={!enabled} variant="outline" className="rounded-2xl h-11 px-3 shrink-0" data-testid={`email-branch-add-${branch.id}`}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {recipients.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`email-branch-chips-${branch.id}`}>
              {recipients.map((e) => (
                <span key={e} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-purple/20 px-2.5 py-1 text-xs">
                  {e}
                  <button type="button" onClick={() => removeRecipient(e)} className="text-brand-inkMuted hover:text-[#C62828]" aria-label={`Remove ${e}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        <Button type="button" onClick={sendTest} disabled={testing || !ownerConfigured} variant="outline" className="rounded-2xl" data-testid={`email-branch-test-${branch.id}`}>
          {testing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-1.5" /> Send test now</>}
        </Button>
        <Button type="button" onClick={save} disabled={saving} className="rounded-2xl bg-brand-purple text-white" data-testid={`email-branch-save-${branch.id}`}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

